export const CUSTOM_FIELD_DATA_TYPES = {
    Number: 1,
    Boolean: 2,
    String: 3,
} as const

export type CustomFieldDataType =
    (typeof CUSTOM_FIELD_DATA_TYPES)[keyof typeof CUSTOM_FIELD_DATA_TYPES]
export type CustomFieldDataTypeName = 'number' | 'boolean' | 'string'

export type CustomFieldEntityType = 'campaign' | 'ad_group' | 'target' | 'ad'

export const CUSTOM_FIELD_ENTITY_OPTIONS: Array<{
    value: CustomFieldEntityType
    label: string
}> = [
    { value: 'campaign', label: 'Campaigns' },
    { value: 'ad_group', label: 'Ad groups' },
    { value: 'target', label: 'Keywords & targets' },
    { value: 'ad', label: 'Ads' },
]

export const ENTITY_TYPE_LABELS: Record<CustomFieldEntityType, string> = Object.fromEntries(
    CUSTOM_FIELD_ENTITY_OPTIONS.map((option) => [option.value, option.label]),
) as Record<CustomFieldEntityType, string>

export interface CustomFieldDefinition {
    definition_id: string
    profile_id: string
    entity_type: CustomFieldEntityType
    key: string
    name: string
    description: string | null
    data_type: CustomFieldDataType
    type: CustomFieldDataTypeName
    version: string
    created_by_user_id: string | null
    updated_by_user_id: string | null
    created_at: string
    updated_at: string
    deleted_at: string | null
}

export interface CustomFieldCatalog {
    profile_id: string
    entity_type: CustomFieldEntityType
    version: string
    definitions: CustomFieldDefinition[]
}

/**
 * Editor catalogs may contain inactive definitions and definitions from other
 * keyspaces. They are never offered in autocomplete, but retaining them lets
 * the editor distinguish deleted and wrong-keyspace references from unknown
 * keys without weakening server-side validation.
 */
export interface CustomFieldEditorContext {
    expectedEntityType: CustomFieldEntityType | null
    definitions: readonly CustomFieldDefinition[]
}

const AD_TYPE_TO_KEYSPACE: Readonly<Record<string, CustomFieldEntityType>> = {
    '1': 'campaign',
    '2': 'ad_group',
    '3': 'ad',
    '21': 'ad',
    '4': 'target',
    '5': 'target',
    '6': 'target',
    '7': 'target',
    '8': 'target',
    '999': 'target',
    campaigns: 'campaign',
    ad_groups: 'ad_group',
    product_ads: 'ad',
    ads: 'ad',
    keywords: 'target',
    targets: 'target',
    keywords_and_targets: 'target',
    campaign_negative_keywords: 'target',
    negative_keywords: 'target',
    negative_targets: 'target',
}

/** Search terms and placements deliberately return null: neither has a v1 value keyspace. */
export const customFieldKeyspaceForAdType = (
    adType: number | string | null | undefined,
): CustomFieldEntityType | null => {
    if (adType === null || adType === undefined) return null
    const normalized = String(adType)
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .trim()
        .toLocaleLowerCase()
        .replace(/[\s-]+/g, '_')
    return AD_TYPE_TO_KEYSPACE[normalized] ?? null
}

export const customFieldDataTypeName = (dataType: CustomFieldDataType): CustomFieldDataTypeName => {
    switch (dataType) {
        case CUSTOM_FIELD_DATA_TYPES.Number:
            return 'number'
        case CUSTOM_FIELD_DATA_TYPES.Boolean:
            return 'boolean'
        case CUSTOM_FIELD_DATA_TYPES.String:
            return 'string'
    }
}
